import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { PLReport } from '../lib/types.js';
import { fmtInt, fmtMoney, mmdd } from '../lib/format.js';
import { costByDay, costTiles } from '../lib/costs-view.js';
import { BarChart, type BarDatum } from '../components/BarChart.js';
import { PLCard } from './costs/pl-card.js';

const RANGES = [
  { label: 'Yesterday', days: -1 },
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

/**
 * The Costs tab (redesign, 2026-09-14): everything cost-side in one place -
 * the bill tiles, cost by day, cost per 1K launches, the four-layer P&L with
 * its coverage header and reconciliation, and the bills. The Stats page keeps
 * only the operating net and the leakage hero.
 */
export function CostsPage() {
  const [days, setDays] = useState(30);
  const pl = useQuery({
    queryKey: ['pl', days],
    queryFn: () => api.get<PLReport>(`/api/pl?days=${days}`),
  });
  const tiles = useMemo(() => costTiles(pl.data), [pl.data]);
  const series = useMemo(() => costByDay(pl.data), [pl.data]);
  const und = pl.data?.undetect ?? null;
  const h = pl.data?.hosting ?? null;
  const other = pl.data?.overheads?.otherOverheads ?? 0;

  const costData: BarDatum[] = series.map((d) => ({
    day: d.day,
    segments: [
      { key: 'undetect', value: d.undetect, color: 'var(--primary)' },
      { key: 'hosting', value: d.hosting, color: 'var(--accent)' },
    ],
    tooltip:
      `${mmdd(d.day)}\n${und ? 'launcher bill' : 'Airman spend'}  ${fmtMoney(d.undetect)}` +
      (h ? `\nhosting  ${fmtMoney(d.hosting)}` : '') +
      (d.per1k != null ? `\nper 1K launches  ${fmtMoney(d.per1k)}` : ''),
  }));
  const unitData: BarDatum[] = series
    .filter((d) => d.per1k != null)
    .map((d) => ({ day: d.day, segments: [], line: d.per1k, tooltip: `${mmdd(d.day)}\nper 1K launches  ${fmtMoney(d.per1k!)}` }));

  return (
    <div>
      <div className="pagehead">
        <h1>Costs</h1>
        <div className="head-right">
          <div className="meta">
            {pl.data ? (
              <>
                {pl.data.since} → {pl.data.upto}
                {pl.data.partialDay ? ` · ${pl.data.partialDay} partial` : ''}
              </>
            ) : (
              'loading…'
            )}
          </div>
        </div>
      </div>
      <div className="filters">
        <div className="seg" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button key={r.days} aria-pressed={days === r.days} onClick={() => setDays(r.days)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {pl.isError ? <div className="warnbox">Could not load the P&amp;L — showing nothing rather than stale guesses.</div> : null}

      <div className="tiles cost-tiles">
        {tiles.map((t) => (
          <div className={'tile' + (t.tone === 'hero' ? ' hero' : t.tone === 'accent' ? ' accent' : '')} key={t.label}>
            <div className="head">
              <div className="label">{t.label}</div>
            </div>
            <div className="value">{t.value}</div>
            <div className="sub">{t.sub}</div>
          </div>
        ))}
      </div>

      <div className="charts">
        <div className="chart-card">
          <div className="card-head">
            <h2>Cost by day</h2>
            <div className="legend">
              <span className="key">
                <span className="sw" style={{ background: 'var(--primary)' }} />
                {und ? 'Undetect' : 'Airman'}
              </span>
              {h ? (
                <span className="key">
                  <span className="sw" style={{ background: 'var(--accent)' }} />
                  Civo hosting
                </span>
              ) : null}
            </div>
          </div>
          <BarChart data={costData} mode="stack" formatValue={(v) => '$' + Math.round(v)} />
          <div className="table-note">
            {und ? 'The launcher bill on mirrored days, Airman’s allocation on the rest.' : 'Airman’s allocation per day; connect Undetect for the bill.'}
            {other > 0 ? ` Other costs (${fmtMoney(other)} this window) are monthly entries charged pro-rata and are not drawn per day - a fixed cost is not a daily signal.` : ''}
          </div>
        </div>
        <div className="chart-card">
          <div className="card-head">
            <h2>Cost per 1K launches</h2>
            <span className="count">{und && und.billPerLaunch != null ? `${fmtMoney(und.billPerLaunch * 1000)} over ${und.coveredDays} mirrored days` : 'needs the launcher bill'}</span>
          </div>
          {unitData.length ? (
            <BarChart data={unitData} mode="stack" formatValue={(v) => '$' + v.toFixed(0)} />
          ) : (
            <div className="table-note">No mirrored day with launches in this window.</div>
          )}
          <div className="table-note">
            Bill ÷ launches, per day: the unit economics of sending traffic. A rising line while revenue per 1K searches is flat is margin leaking
            through the launcher.
          </div>
        </div>
      </div>

      <PLCard pl={pl.data} />

      {pl.data ? (
        <div className="table-card">
          <div className="table-head">
            <h2>The bills</h2>
          </div>
          <div className="bills">
            <div className="pl-row">
              <span>
                {und ? 'Undetect (launcher)' : 'Airman (allocation)'}{' '}
                <span className="count">{und ? 'the control total — every other cost figure is its breakdown' : 'per-destination spend; connect Undetect for the bill'}</span>
              </span>
              <span>{fmtMoney(pl.data.operating.cost - (und?.aiCost ?? 0))}</span>
            </div>
            {und && und.aiCost > 0 ? (
              <div className="pl-row">
                <span>
                  Airman AI <span className="count">conversion-action calls on the mirrored days · not in the launcher bill</span>
                </span>
                <span>{fmtMoney(und.aiCost)}</span>
              </div>
            ) : null}
            {h ? (
              <div className="pl-row">
                <span>
                  Civo (hosting) <span className="count">mirrored at list rates · {h.vatPct > 0 ? `incl. ${h.vatPct}% VAT` : 'ex-VAT'} · {fmtInt(h.hours)} h</span>
                </span>
                <span>{fmtMoney(h.cost)}</span>
              </div>
            ) : null}
            {(pl.data.overheads?.items ?? []).map((i) => (
              <div className="pl-row" key={i.id}>
                <span>
                  {i.name} <span className="count">fixed monthly {fmtMoney(i.monthlyUsd)}, pro-rata by day</span>
                </span>
                <span>{fmtMoney(i.charged)}</span>
              </div>
            ))}
            <div className="pl-row net">
              <span>Total cost</span>
              <span>{tiles[0]?.value ?? '—'}</span>
            </div>
          </div>
          <div className="table-note">Other costs are entered under Configuration → Buy side; invoices are checked against the mirrors there too.</div>
        </div>
      ) : null}
    </div>
  );
}
